import {
  GroupShapeIcon,
  type GroupShape,
} from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import {
  Button,
  Checkbox,
  ColorInput,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo } from "react";

export function WorkspaceModalMapPoint({
  onClose,
  title = "Configure Point",
  pointId,
  pointValue,
  initialColor,
  initialShape,
  initialVisible,
}: {
  onClose: () => void;
  title?: string;
  pointId?: string;
  pointValue?: string;
  initialColor?: string;
  initialShape?: GroupShape;
  initialVisible?: boolean;
}) {
  const activeDataflowId = useActiveDataflowId();
  const setPointConfig = useMapStore((state) => state.setPointConfig);
  const getPointConfig = useMapStore((state) => state.getPointConfig);
  const getGroupConfig = useMapStore((state) => state.getGroupConfig);
  const setVisibleGroups = useMapStore((state) => state.setVisibleGroups);
  const visibleGroups = useMapStore(
    (state) => state.groups[activeDataflowId || ""]?.visibleGroups || []
  );
  const getChunks = useMapStore((state) => state.getChunks);

  const currentConfig =
    activeDataflowId && pointId
      ? getPointConfig(activeDataflowId, pointId)
      : null;

  // Get group config as fallback if point config doesn't exist
  const groupConfig =
    activeDataflowId && pointValue
      ? getGroupConfig(activeDataflowId, pointValue)
      : null;

  // Check if point is in a chunk
  const chunkContainingPoint = activeDataflowId
    ? getChunks(activeDataflowId)?.find((chunk) =>
        chunk.values.includes(pointValue || "")
      )
    : null;

  const isVisible = useMemo(() => {
    if (pointValue) {
      return visibleGroups.includes(pointValue);
    }
    return initialVisible !== undefined ? initialVisible : true;
  }, [pointValue, visibleGroups, initialVisible]);

  const form = useForm<{
    pointValue: string;
    color: string;
    shape: GroupShape;
    visible: boolean;
  }>({
    initialValues: {
      pointValue: pointValue || "",
      color:
        initialColor || currentConfig?.color || groupConfig?.color || "#003399",
      shape:
        initialShape || currentConfig?.shape || groupConfig?.shape || "circle",
      visible: initialVisible !== undefined ? initialVisible : isVisible,
    },
    validate: {
      pointValue: (value) =>
        !value || value.trim() === "" ? "Point value is required" : null,
      color: (value) => (!value ? "Color is required" : null),
    },
  });

  const handleSubmit = (values: {
    pointValue: string;
    color: string;
    shape: GroupShape;
    visible: boolean;
  }) => {
    if (!activeDataflowId || !pointId) {
      logger.error(
        "Cannot save point config: missing activeDataflowId or pointId",
        {
          activeDataflowId,
          pointId,
        }
      );
      return;
    }

    // Update point config
    setPointConfig(activeDataflowId, pointId, {
      color: values.color,
      shape: values.shape,
      isVisible: values.visible,
    });

    // Update visibility in visibleGroups
    const currentVisible = new Set<string>(visibleGroups);
    if (values.visible) {
      if (values.pointValue) {
        currentVisible.add(values.pointValue);
      }
    } else {
      if (values.pointValue) {
        currentVisible.delete(values.pointValue);
      }
    }
    setVisibleGroups(activeDataflowId, Array.from(currentVisible));

    // If point is in a chunk, trigger recalculation
    // Note: This is a simplified approach - in practice, you might want to
    // update the chunk config or handle this differently
    if (chunkContainingPoint && activeDataflowId) {
      // Recompute chunks to ensure consistency
      // This would need the unique values - for now we'll skip automatic recalculation
      // and rely on manual refresh or the next data update
    }

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <TextInput
            label="Point Value"
            placeholder="Point identifier"
            {...form.getInputProps("pointValue")}
            required
            error={form.errors.pointValue}
            readOnly
            description="Point identifier (read-only)"
          />

          <ColorInput
            label="Point Color"
            format="hex"
            swatches={groupColors}
            {...form.getInputProps("color")}
            required
            error={form.errors.color}
          />

          <div>
            <Text size="sm" fw={500} mb="xs">
              Shape
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                {
                  value: "circle",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="circle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "square",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="square"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "triangle",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="triangle"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "star",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="star"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
                {
                  value: "diamond",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <GroupShapeIcon
                        shape="diamond"
                        color={form.values.color}
                        size={20}
                      />
                    </div>
                  ),
                },
              ]}
              {...form.getInputProps("shape")}
            />
          </div>

          <Checkbox
            label="Visible"
            {...form.getInputProps("visible", { type: "checkbox" })}
          />

          {chunkContainingPoint && (
            <Text size="xs" c="dimmed">
              Note: This point is part of a cluster. Individual overrides will
              take precedence.
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
